// src/pages/UniversityLanding.js
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LandingHeader from '../../components/LandingHeader';
import AuthDialog from '../../components/AuthDialog';
import {
    Rocket, Database, Brain,
    TrendingUp, Award,
    Users,
    FileText, School, CheckCircle,
    Layers, Share2, Lightbulb,
    BookOpen, MessageCircle
} from 'lucide-react';

const UniversityLanding = () => {
    const { t } = useTranslation();
    const [showAuthDialog, setShowAuthDialog] = useState(false);
    const [initialAuthMode, setInitialAuthMode] = useState('register');
    const demoSectionRef = useRef(null);
    const videoRef = useRef(null);
    const [imageErrors, setImageErrors] = useState({});
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [activeTab, setActiveTab] = useState('university'); // для вкладок в секции benefits
    const [activeFeaturesTab, setActiveFeaturesTab] = useState('labs'); // для вкладок в секции features
    const [activeTestimonialTab, setActiveTestimonialTab] = useState(0); // для переключения отзывов

    // Обработчик для кнопки регистрации
    const handleRegisterClick = () => {
        setInitialAuthMode('register');
        setShowAuthDialog(true);
    };

    // Функция обработки ошибок загрузки изображений
    const handleImageError = (imageName) => {
        setImageErrors(prev => ({
            ...prev,
            [imageName]: true
        }));
        console.error(`Ошибка загрузки изображения: ${imageName}`);
    };

    // Отложенная загрузка видео
    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        const options = {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const source = document.createElement('source');
                    source.src = '/demos/product_demo.mp4';
                    source.type = 'video/mp4';
                    videoElement.appendChild(source);
                    videoElement.load();
                    videoElement.play().catch(err => console.error("Ошибка воспроизведения видео:", err));
                    observer.unobserve(videoElement);
                }
            });
        }, options);

        observer.observe(videoElement);

        return () => {
            if (videoElement) {
                observer.unobserve(videoElement);
            }
        };
    }, []);

    // Данные для секции вызовов
    const challenges = [
        {
            icon: <FileText className="h-10 w-10 text-purple-500" />,
            title: t('university.challenges.manual_grading.title'),
            description: t('university.challenges.manual_grading.description')
        },
        {
            icon: <Layers className="h-10 w-10 text-purple-500" />,
            title: t('university.challenges.hybrid_format.title'),
            description: t('university.challenges.hybrid_format.description')
        },
        {
            icon: <Users className="h-10 w-10 text-purple-500" />,
            title: t('university.challenges.personalization.title'),
            description: t('university.challenges.personalization.description')
        },
        {
            icon: <Share2 className="h-10 w-10 text-purple-500" />,
            title: t('university.challenges.unified_platform.title'),
            description: t('university.challenges.unified_platform.description')
        }
    ];

    // Данные для секции последствий
    const consequences = [
        {
            icon: <TrendingUp className="h-10 w-10 text-red-500 transform rotate-180" />,
            title: t('university.consequences.engagement.title'),
            description: t('university.consequences.engagement.description')
        },
        {
            icon: <Users className="h-10 w-10 text-red-500" />,
            title: t('university.consequences.employment.title'),
            description: t('university.consequences.employment.description')
        },
        {
            icon: <School className="h-10 w-10 text-red-500" />,
            title: t('university.consequences.ranking.title'),
            description: t('university.consequences.ranking.description')
        }
    ];

    // Отзывы для карусели
    const testimonials = [
        {
            quote: t('university.testimonials.quote1'),
            name: t('university.testimonials.name1'),
            title: t('university.testimonials.title1'),
            initials: 'АП'
        },
        {
            quote: t('university.testimonials.quote2'),
            name: t('university.testimonials.name2'),
            title: t('university.testimonials.title2'),
            initials: 'МС'
        }
    ];

    return (
        <div className="min-h-screen min-w-screen w-full overflow-hidden bg-gray-900 text-white">
            <LandingHeader onLoginSuccess={() => {}} />

            {/* Hero Section */}
            <section className="pt-32 pb-20 relative overflow-hidden">
                {/* 3D Анимированный фон */}
                <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 via-purple-800/20 to-gray-900/10" />
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,theme(colors.purple.500/20),transparent_70%)]" />

                    {/* 3D плавающие элементы */}
                    <div className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full bg-purple-500/5 blur-2xl animate-pulse" />
                    <div className="absolute bottom-1/3 left-1/3 w-96 h-96 rounded-full bg-purple-700/5 blur-3xl animate-pulse" />
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="inline-block px-4 py-1 bg-purple-900/50 backdrop-blur-sm rounded-full text-purple-300 font-semibold mb-4 text-sm transform hover:scale-105 transition-transform">
                            {t('university.hero.tag')}
                        </div>

                        <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight tracking-tight text-white">
                            {t('university.hero.title')}
                        </h1>

                        <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
                            {t('university.hero.subtitle')}
                        </p>

                        <div className="flex justify-center">
                            <button
                                onClick={handleRegisterClick}
                                className="group relative px-10 py-5 rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold text-lg transition-all hover:scale-105 shadow-lg hover:shadow-purple-500/20 overflow-hidden"
                            >
                                {/* Анимация фона кнопки */}
                                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-purple-700 opacity-0 group-hover:opacity-100 transition-opacity" />

                                <span className="relative z-10 flex items-center justify-center gap-3">
                                    {t('university.hero.start_free')}
                                    <Rocket className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Integration Partners Section */}
            <section className="py-12 bg-gray-800/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-900/10 to-gray-900/10" />

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-8">
                        <p className="text-gray-300 text-lg">
                            {t('university.integration.title')}
                        </p>
                    </div>
                    <div className="flex flex-wrap justify-center items-center gap-12">
                        <div className="bg-gray-400/70 px-8 py-4 rounded-lg hover:bg-gray-800/90 transition-colors">
                            {!imageErrors['moodle_logo'] ? (
                                <img
                                    src="/images/moodle_logo.png"
                                    alt="Moodle"
                                    className="h-10 opacity-80 hover:opacity-100 transition-opacity"
                                    onError={() => handleImageError('moodle_logo')}
                                />
                            ) : (
                                <div className="h-10 w-32 flex items-center justify-center">
                                    <span className="text-purple-400 font-bold">Moodle</span>
                                </div>
                            )}
                        </div>
                        <div className="bg-gray-400/70 px-8 py-4 rounded-lg hover:bg-gray-800/90 transition-colors">
                            {!imageErrors['canvas_logo'] ? (
                                <img
                                    src="/images/canvas_logo.png"
                                    alt="Canvas"
                                    className="h-10 opacity-80 hover:opacity-100 transition-opacity"
                                    onError={() => handleImageError('canvas_logo')}
                                />
                            ) : (
                                <div className="h-10 w-32 flex items-center justify-center">
                                    <span className="text-purple-400 font-bold">Canvas</span>
                                </div>
                            )}
                        </div>
                        <div className="bg-gray-400/70 px-8 py-4 rounded-lg hover:bg-gray-800/90 transition-colors">
                            {!imageErrors['blackboard_logo'] ? (
                                <img
                                    src="/images/blackboard_logo.png"
                                    alt="Blackboard"
                                    className="h-10 opacity-80 hover:opacity-100 transition-opacity"
                                    onError={() => handleImageError('blackboard_logo')}
                                />
                            ) : (
                                <div className="h-10 w-32 flex items-center justify-center">
                                    <span className="text-purple-400 font-bold">Blackboard</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Challenges Section */}
            <section className="py-20 bg-gray-900/80 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,theme(colors.purple.500/5),transparent_50%)]" />

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white">
                            {t('university.challenges.title')}
                        </h2>
                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            {t('university.challenges.subtitle')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        {challenges.map((challenge, index) => (
                            <div
                                key={index}
                                className="group relative overflow-hidden bg-gray-800/30 backdrop-blur-sm border border-gray-700 p-8 rounded-xl hover:border-purple-500/50 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-purple-500/5"
                            >
                                {/* Декоративные элементы фона */}
                                <div className="absolute bottom-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                                <div className="relative z-10">
                                    {/* Контейнер для иконки с анимацией */}
                                    <div className="mb-6 w-16 h-16 rounded-xl bg-purple-500/10 flex items-center justify-center transform group-hover:scale-110 group-hover:bg-purple-500/20 transition-all duration-300 group-hover:rotate-3">
                                        {challenge.icon}
                                    </div>

                                    <h3 className="text-xl font-bold mb-4 group-hover:text-purple-400 transition-colors">
                                        {challenge.title}
                                    </h3>

                                    <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                        {challenge.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Consequences Section */}
            <section className="py-20 bg-gradient-to-b from-gray-800/40 to-gray-900/40 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-gray-900" />

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white">
                            {t('university.consequences.title')}
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            {t('university.consequences.subtitle')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                        {consequences.map((consequence, index) => (
                            <div
                                key={index}
                                className="group relative overflow-hidden bg-gray-800/30 backdrop-blur-sm border border-gray-700 p-8 rounded-xl hover:border-red-500/50 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-red-500/5"
                            >
                                {/* Декоративные элементы фона */}
                                <div className="absolute bottom-0 right-0 w-32 h-32 bg-red-500/5 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                                <div className="relative z-10">
                                    <div className="mb-6 w-16 h-16 rounded-xl bg-red-500/10 flex items-center justify-center transform group-hover:scale-110 group-hover:bg-red-500/20 transition-all duration-300 group-hover:rotate-3">
                                        {consequence.icon}
                                    </div>
                                    <h3 className="text-xl font-bold mb-4 group-hover:text-red-400 transition-colors">
                                        {consequence.title}
                                    </h3>
                                    <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                        {consequence.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Solution Section */}
            <section className="py-20 bg-gray-900 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,theme(colors.purple.500/10),transparent_70%)]" />

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <div className="inline-block p-2 px-4 bg-purple-500/10 backdrop-blur-sm rounded-full text-purple-400 font-semibold mb-4 transform hover:scale-105 transition-transform">
                            {t('university.solution.tag')}
                        </div>
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white">
                            {t('university.solution.title')}
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            {t('university.solution.subtitle')}
                        </p>
                    </div>

                    <div ref={demoSectionRef} className="max-w-4xl mx-auto mb-16">
                        <div className="relative rounded-xl overflow-hidden shadow-2xl border border-gray-700 group">
                            {/* Блики и эффекты вокруг видео */}
                            <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/30 to-purple-600/30 blur-xl opacity-70 group-hover:opacity-100 transition-opacity duration-500"></div>

                            <div className="relative">
                                <video
                                    ref={videoRef}
                                    autoPlay={false}
                                    loop
                                    muted
                                    playsInline
                                    className="w-full aspect-video object-cover"
                                    poster="/images/dvakota_university_poster.jpg"
                                    onPlay={() => setIsVideoPlaying(true)}
                                    onPause={() => setIsVideoPlaying(false)}
                                >
                                    <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                                        <p className="text-white">{t('common.loading')}</p>
                                    </div>
                                </video>

                                {/* Наложение для имитации интерактивности */}
                                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent opacity-80 group-hover:opacity-50 transition-opacity"></div>

                                {/* Иконка Play для видео - показывается только если видео не воспроизводится */}
                                {!isVideoPlaying && (
                                    <div
                                        className="absolute inset-0 flex items-center justify-center cursor-pointer"
                                        onClick={() => {
                                            if (videoRef.current) {
                                                videoRef.current.play();
                                            }
                                        }}
                                    >
                                        <div className="w-16 h-16 md:w-20 md:h-20 bg-purple-500/80 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-8 h-8 md:w-10 md:h-10">
                                                <path d="M8 5v14l11-7z" />
                                            </svg>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Вкладки для категорий возможностей */}
                    <div className="max-w-6xl mx-auto mb-16">
                        <div className="flex flex-wrap justify-center mb-8 gap-2">
                            <button
                                className={`px-6 py-3 rounded-full font-semibold transition-all ${
                                    activeFeaturesTab === 'labs'
                                        ? 'bg-purple-500 text-white'
                                        : 'bg-gray-800/70 text-gray-300 hover:bg-gray-700/70'
                                }`}
                                onClick={() => setActiveFeaturesTab('labs')}
                            >
                                {t('university.features.labs.title')}
                            </button>
                            <button
                                className={`px-6 py-3 rounded-full font-semibold transition-all ${
                                    activeFeaturesTab === 'ai'
                                        ? 'bg-purple-500 text-white'
                                        : 'bg-gray-800/70 text-gray-300 hover:bg-gray-700/70'
                                }`}
                                onClick={() => setActiveFeaturesTab('ai')}
                            >
                                {t('university.features.ai_tutor.title')}
                            </button>
                            <button
                                className={`px-6 py-3 rounded-full font-semibold transition-all ${
                                    activeFeaturesTab === 'platform'
                                        ? 'bg-purple-500 text-white'
                                        : 'bg-gray-800/70 text-gray-300 hover:bg-gray-700/70'
                                }`}
                                onClick={() => setActiveFeaturesTab('platform')}
                            >
                                {t('university.features.integration.title')}
                            </button>
                        </div>

                        {/* Содержимое вкладок */}
                        {activeFeaturesTab === 'labs' && (
                            <div className="bg-gray-800/30 backdrop-blur-sm border border-purple-500/20 p-6 rounded-2xl shadow-lg transform transition-all">
                                <div className="flex flex-col md:flex-row gap-8 items-center">
                                    <div className="md:w-1/2">
                                        <h3 className="text-2xl font-bold mb-4 text-purple-400">{t('university.features.labs.title')}</h3>
                                        <p className="text-gray-300 mb-6">
                                            {t('university.features.labs.description')}
                                        </p>
                                        <ul className="space-y-2">
                                            <li className="flex items-start gap-2">
                                                <span className="text-purple-500 mt-1">•</span>
                                                <span className="text-gray-300">Микросервисные архитектуры</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-purple-500 mt-1">•</span>
                                                <span className="text-gray-300">Базы данных (SQL и NoSQL)</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-purple-500 mt-1">•</span>
                                                <span className="text-gray-300">Облачные сервисы</span>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className="md:w-1/2 bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
                                        <div className="w-full aspect-video bg-gray-800 flex items-center justify-center">
                                            <Database className="h-16 w-16 text-purple-500 opacity-50" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeFeaturesTab === 'ai' && (
                            <div className="bg-gray-800/30 backdrop-blur-sm border border-purple-500/20 p-6 rounded-2xl shadow-lg transform transition-all">
                                <div className="flex flex-col md:flex-row gap-8 items-center">
                                    <div className="md:w-1/2">
                                        <h3 className="text-2xl font-bold mb-4 text-purple-400">{t('university.features.ai_tutor.title')}</h3>
                                        <p className="text-gray-300 mb-6">
                                            {t('university.features.ai_tutor.description')}
                                        </p>
                                        <ul className="space-y-2">
                                            <li className="flex items-start gap-2">
                                                <span className="text-purple-500 mt-1">•</span>
                                                <span className="text-gray-300">Персональный AI-тьютор 24/7</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-purple-500 mt-1">•</span>
                                                <span className="text-gray-300">Автоматическая проверка заданий</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-purple-500 mt-1">•</span>
                                                <span className="text-gray-300">Генерация тестов и заданий</span>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className="md:w-1/2 bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
                                        <div className="w-full aspect-video bg-gray-800 flex items-center justify-center">
                                            <Brain className="h-16 w-16 text-purple-500 opacity-50" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeFeaturesTab === 'platform' && (
                            <div className="bg-gray-800/30 backdrop-blur-sm border border-purple-500/20 p-6 rounded-2xl shadow-lg transform transition-all">
                                <div className="flex flex-col md:flex-row gap-8 items-center">
                                    <div className="md:w-1/2">
                                        <h3 className="text-2xl font-bold mb-4 text-purple-400">{t('university.features.integration.title')}</h3>
                                        <p className="text-gray-300 mb-6">
                                            {t('university.features.integration.description')}
                                        </p>
                                        <ul className="space-y-2">
                                            <li className="flex items-start gap-2">
                                                <span className="text-purple-500 mt-1">•</span>
                                                <span className="text-gray-300">Moodle, Canvas, Blackboard</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-purple-500 mt-1">•</span>
                                                <span className="text-gray-300">Единый вход через SSO</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-purple-500 mt-1">•</span>
                                                <span className="text-gray-300">Автоматическая синхронизация оценок</span>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className="md:w-1/2 bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
                                        <div className="w-full aspect-video bg-gray-800 flex items-center justify-center">
                                            <Layers className="h-16 w-16 text-purple-500 opacity-50" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* How It Works - Teachers */}
            <section className="py-20 bg-gray-800/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-900/10 to-gray-900/10" />

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white">
                            {t('university.how_it_works.teacher.title')}
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            {t('university.how_it_works.teacher.subtitle')}
                        </p>
                    </div>

                    <div className="max-w-6xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                            <div className="order-2 lg:order-1">
                                <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden shadow-xl relative group">
                                    {/* Эффект блика на изображении */}
                                    <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500/30 to-purple-600/30 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>

                                    <div className="relative">
                                        {!imageErrors['university_teacher'] ? (
                                            <img
                                                src="/images/university_teacher.png"
                                                alt={t('university.how_it_works.teacher.title')}
                                                className="w-full object-cover"
                                                onError={() => handleImageError('university_teacher')}
                                            />
                                        ) : (
                                            <div className="w-full aspect-video bg-gray-800 flex items-center justify-center">
                                                <School className="h-16 w-16 text-purple-500 opacity-50" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="order-1 lg:order-2">
                                <div className="space-y-8">
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-500 font-bold text-xl">
                                            1
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-purple-400">
                                                {t('university.how_it_works.teacher.step1.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('university.how_it_works.teacher.step1.description')}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-500 font-bold text-xl">
                                            2
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-purple-400">
                                                {t('university.how_it_works.teacher.step2.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('university.how_it_works.teacher.step2.description')}
                                            </p>
                                            <ul className="mt-2 space-y-2 text-gray-400">
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 mt-1">•</span>
                                                    {t('university.how_it_works.teacher.step2.option1')}
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 mt-1">•</span>
                                                    {t('university.how_it_works.teacher.step2.option2')}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-500 font-bold text-xl">
                                            3
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-purple-400">
                                                {t('university.how_it_works.teacher.step3.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('university.how_it_works.teacher.step3.description')}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-500 font-bold text-xl">
                                            4
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-purple-400">
                                                {t('university.how_it_works.teacher.step4.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('university.how_it_works.teacher.step4.description')}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-500 font-bold text-xl">
                                            5
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-purple-400">
                                                {t('university.how_it_works.teacher.step5.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('university.how_it_works.teacher.step5.description')}
                                            </p>
                                            <ul className="mt-2 space-y-2 text-gray-400">
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 mt-1">•</span>
                                                    {t('university.how_it_works.teacher.step5.option1')}
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 mt-1">•</span>
                                                    {t('university.how_it_works.teacher.step5.option2')}
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 mt-1">•</span>
                                                    {t('university.how_it_works.teacher.step5.option3')}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works - Students */}
            <section className="py-20 bg-gray-900 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-gray-900" />

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white">
                            {t('university.how_it_works.student.title')}
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            {t('university.how_it_works.student.subtitle')}
                        </p>
                    </div>

                    <div className="max-w-6xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                            <div>
                                <div className="space-y-8">
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-500 font-bold text-xl">
                                            1
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-purple-400">
                                                {t('university.how_it_works.student.step1.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('university.how_it_works.student.step1.description')}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-500 font-bold text-xl">
                                            2
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-purple-400">
                                                {t('university.how_it_works.student.step2.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('university.how_it_works.student.step2.description')}
                                            </p>
                                            <ul className="mt-2 space-y-2 text-gray-400">
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 mt-1">•</span>
                                                    {t('university.how_it_works.student.step2.option1')}
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 mt-1">•</span>
                                                    {t('university.how_it_works.student.step2.option2')}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-500 font-bold text-xl">
                                            3
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-purple-400">
                                                {t('university.how_it_works.student.step3.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('university.how_it_works.student.step3.description')}
                                            </p>
                                            <ul className="mt-2 space-y-2 text-gray-400">
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 mt-1">•</span>
                                                    {t('university.how_it_works.student.step3.option1')}
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 mt-1">•</span>
                                                    {t('university.how_it_works.student.step3.option2')}
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 mt-1">•</span>
                                                    {t('university.how_it_works.student.step3.option3')}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-500 font-bold text-xl">
                                            4
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2 text-purple-400">
                                                {t('university.how_it_works.student.step4.title')}
                                            </h3>
                                            <p className="text-gray-300">
                                                {t('university.how_it_works.student.step4.description')}
                                            </p>
                                            <ul className="mt-2 space-y-2 text-gray-400">
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 mt-1">•</span>
                                                    {t('university.how_it_works.student.step4.option1')}
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 mt-1">•</span>
                                                    {t('university.how_it_works.student.step4.option2')}
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 mt-1">•</span>
                                                    {t('university.how_it_works.student.step4.option3')}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden shadow-xl relative group">
                                    {/* Эффект блика на изображении */}
                                    <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500/30 to-purple-600/30 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>

                                    <div className="relative">
                                        {!imageErrors['university_student'] ? (
                                            <img
                                                src="/images/university_student.png"
                                                alt={t('university.how_it_works.student.title')}
                                                className="w-full object-cover"
                                                onError={() => handleImageError('university_student')}
                                            />
                                        ) : (
                                            <div className="w-full aspect-video bg-gray-800 flex items-center justify-center">
                                                <Users className="h-16 w-16 text-purple-500 opacity-50" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Section - Статистика и интерактивные вкладки */}
            <section className="py-20 bg-gradient-to-b from-gray-800/40 to-gray-900/40 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,theme(colors.green.500/10),transparent_70%)]" />

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white">
                            {t('university.benefits.title')}
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            {t('university.benefits.subtitle')}
                        </p>
                    </div>

                    {/* Статистика с анимированными счетчиками */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-16 max-w-5xl mx-auto">
                        <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-sm p-8 rounded-2xl border border-green-500/20 transform transition-all hover:-translate-y-1 hover:shadow-lg hover:shadow-green-500/5 text-center">
                            <div className="text-4xl md:text-5xl font-bold text-green-400 mb-4">+75%</div>
                            <p className="text-xl text-gray-300">{t('university.stats.employment')}</p>
                        </div>

                        <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-sm p-8 rounded-2xl border border-green-500/20 transform transition-all hover:-translate-y-1 hover:shadow-lg hover:shadow-green-500/5 text-center">
                            <div className="text-4xl md:text-5xl font-bold text-green-400 mb-4">-40%</div>
                            <p className="text-xl text-gray-300">{t('university.stats.workload')}</p>
                        </div>

                        <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-sm p-8 rounded-2xl border border-green-500/20 transform transition-all hover:-translate-y-1 hover:shadow-lg hover:shadow-green-500/5 text-center">
                            <div className="text-4xl md:text-5xl font-bold text-green-400 mb-4">24/7</div>
                            <p className="text-xl text-gray-300">{t('university.stats.availability')}</p>
                        </div>
                    </div>

                    {/* Преимущества в виде интерактивных вкладок */}
                    <div className="max-w-5xl mx-auto bg-gray-800/20 rounded-2xl p-2 backdrop-blur-sm">
                        <div className="flex flex-wrap justify-center mb-6 border-b border-gray-700">
                            <button
                                className={`px-6 py-3 font-semibold rounded-t-lg transition-colors ${activeTab === 'university' ? 'bg-green-500/20 text-green-300' : 'text-gray-400 hover:text-gray-300'}`}
                                onClick={() => setActiveTab('university')}
                            >
                                {t('university.tabs.university')}
                            </button>
                            <button
                                className={`px-6 py-3 font-semibold rounded-t-lg transition-colors ${activeTab === 'teachers' ? 'bg-green-500/20 text-green-300' : 'text-gray-400 hover:text-gray-300'}`}
                                onClick={() => setActiveTab('teachers')}
                            >
                                {t('university.tabs.teachers')}
                            </button>
                            <button
                                className={`px-6 py-3 font-semibold rounded-t-lg transition-colors ${activeTab === 'students' ? 'bg-green-500/20 text-green-300' : 'text-gray-400 hover:text-gray-300'}`}
                                onClick={() => setActiveTab('students')}
                            >
                                {t('university.tabs.students')}
                            </button>
                        </div>

                        <div className="p-6">
                            {activeTab === 'university' && (
                                <ul className="space-y-4">
                                    <li className="flex items-start gap-4 group">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 group-hover:bg-green-500/20 transition-all">
                                            <Award className="h-6 w-6 text-green-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors">
                                                {t('university.university_benefits.ranking.title')}
                                            </h3>
                                            <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                {t('university.university_benefits.ranking.description')}
                                            </p>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-4 group">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 group-hover:bg-green-500/20 transition-all">
                                            <TrendingUp className="h-6 w-6 text-green-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors">
                                                {t('university.university_benefits.attractiveness.title')}
                                            </h3>
                                            <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                {t('university.university_benefits.attractiveness.description')}
                                            </p>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-4 group">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 group-hover:bg-green-500/20 transition-all">
                                            <Layers className="h-6 w-6 text-green-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors">
                                                {t('university.university_benefits.infrastructure.title')}
                                            </h3>
                                            <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                {t('university.university_benefits.infrastructure.description')}
                                            </p>
                                        </div>
                                    </li>
                                </ul>
                            )}

                            {activeTab === 'teachers' && (
                                <ul className="space-y-4">
                                    <li className="flex items-start gap-4 group">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 group-hover:bg-green-500/20 transition-all">
                                            <Lightbulb className="h-6 w-6 text-green-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors">
                                                {t('university.teachers_benefits.reduced_workload.title')}
                                            </h3>
                                            <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                {t('university.teachers_benefits.reduced_workload.description')}
                                            </p>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-4 group">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 group-hover:bg-green-500/20 transition-all">
                                            <BookOpen className="h-6 w-6 text-green-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors">
                                                {t('university.teachers_benefits.ready_materials.title')}
                                            </h3>
                                            <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                {t('university.teachers_benefits.ready_materials.description')}
                                            </p>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-4 group">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 group-hover:bg-green-500/20 transition-all">
                                            <MessageCircle className="h-6 w-6 text-green-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors">
                                                {t('university.teachers_benefits.analytics.title')}
                                            </h3>
                                            <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                {t('university.teachers_benefits.analytics.description')}
                                            </p>
                                        </div>
                                    </li>
                                </ul>
                            )}

                            {activeTab === 'students' && (
                                <ul className="space-y-4">
                                    <li className="flex items-start gap-4 group">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 group-hover:bg-green-500/20 transition-all">
                                            <Brain className="h-6 w-6 text-green-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors">
                                                {t('university.students_benefits.personalized_learning.title')}
                                            </h3>
                                            <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                {t('university.students_benefits.personalized_learning.description')}
                                            </p>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-4 group">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 group-hover:bg-green-500/20 transition-all">
                                            <Database className="h-6 w-6 text-green-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors">
                                                {t('university.students_benefits.real_systems.title')}
                                            </h3>
                                            <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                {t('university.students_benefits.real_systems.description')}
                                            </p>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-4 group">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 group-hover:bg-green-500/20 transition-all">
                                            <CheckCircle className="h-6 w-6 text-green-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white group-hover:text-green-400 transition-colors">
                                                {t('university.students_benefits.job_readiness.title')}
                                            </h3>
                                            <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                {t('university.students_benefits.job_readiness.description')}
                                            </p>
                                        </div>
                                    </li>
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials Section - Интерактивная карусель */}
            <section className="py-20 bg-gray-900 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,theme(colors.purple.500/5),transparent_70%)]" />

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white">
                            {t('university.testimonials.title')}
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            {t('university.testimonials.subtitle')}
                        </p>
                    </div>

                    <div className="max-w-5xl mx-auto relative">
                        {/* Карусель отзывов */}
                        <div className="bg-gradient-to-br from-gray-800/30 to-gray-900/30 backdrop-blur-sm p-8 md:p-10 rounded-2xl border border-purple-500/20 shadow-xl">
                            <div className="flex justify-center mb-6">
                                {testimonials.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setActiveTestimonialTab(index)}
                                        className={`w-3 h-3 mx-2 rounded-full transition-all ${
                                            activeTestimonialTab === index
                                                ? 'bg-purple-500 w-10'
                                                : 'bg-gray-600 hover:bg-gray-500'
                                        }`}
                                        aria-label={`Отзыв ${index + 1}`}
                                    />
                                ))}
                            </div>

                            <div className="mb-8">
                                <p className="text-xl text-gray-300 italic mb-8 relative">
                                    <span className="absolute -top-4 -left-3 text-5xl text-purple-500/50 font-serif">"</span>
                                    {testimonials[activeTestimonialTab].quote}
                                    <span className="absolute -bottom-4 -right-3 text-5xl text-purple-500/50 font-serif">"</span>
                                </p>
                            </div>

                            <div className="flex items-center gap-4 mt-6">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-bold">
                                    {testimonials[activeTestimonialTab].initials}
                                </div>
                                <div>
                                    <p className="font-semibold text-white">{testimonials[activeTestimonialTab].name}</p>
                                    <p className="text-gray-400 text-sm">{testimonials[activeTestimonialTab].title}</p>
                                </div>
                            </div>
                        </div>

                        {/* Кнопки навигации */}
                        <div className="flex justify-between absolute top-1/2 left-0 right-0 transform -translate-y-1/2 pointer-events-none">
                            <button
                                onClick={() => setActiveTestimonialTab(prev => (prev > 0 ? prev - 1 : testimonials.length - 1))}
                                className="w-12 h-12 rounded-full bg-gray-900/70 border border-purple-500/30 flex items-center justify-center text-white pointer-events-auto hover:bg-gray-800/70 transition-colors transform -translate-x-6"
                                aria-label="Предыдущий отзыв"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <button
                                onClick={() => setActiveTestimonialTab(prev => (prev < testimonials.length - 1 ? prev + 1 : 0))}
                                className="w-12 h-12 rounded-full bg-gray-900/70 border border-purple-500/30 flex items-center justify-center text-white pointer-events-auto hover:bg-gray-800/70 transition-colors transform translate-x-6"
                                aria-label="Следующий отзыв"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 relative w-full overflow-hidden">
                {/* Фон с органическими формами */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 to-purple-800/30">
                    <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
                        {/* SVG органические формы */}
                        <svg className="absolute top-0 left-0 w-full h-full opacity-20" viewBox="0 0 1000 1000" preserveAspectRatio="none">
                            <path d="M0,500 C150,300 350,0 500,500 C650,1000 850,700 1000,500 L1000,1000 L0,1000 Z" fill="url(#purple-gradient)" />
                            <defs>
                                <linearGradient id="purple-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#A855F7" />
                                    <stop offset="100%" stopColor="#6B21A8" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                </div>

                <div className="container relative z-10 mx-auto px-6">
                    <div className="bg-gray-900/80 backdrop-blur-lg border border-purple-500/20 rounded-3xl p-12 md:p-16 shadow-2xl max-w-5xl mx-auto">
                        <h2 className="text-4xl md:text-6xl font-bold mb-6 text-center text-white">
                            {t('university.cta.title')}
                        </h2>

                        <p className="text-xl text-gray-300 mb-12 text-center max-w-3xl mx-auto">
                            {t('university.cta.subtitle')}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-6 justify-center">
                            <button
                                onClick={handleRegisterClick}
                                className="group relative px-10 py-5 rounded-xl bg-gradient-to-r from-purple-500 to-purple-700 text-white font-semibold text-lg transition-all hover:scale-105 shadow-lg hover:shadow-purple-500/25"
                            >
                                <span className="relative z-10 flex items-center justify-center gap-3">
                                    {t('university.cta.button')}
                                    <Rocket className="w-5 h-5 group-hover:animate-bounce" />
                                </span>
                            </button>

                            <a
                                href="mailto:info@dvakota.com"
                                className="px-10 py-5 rounded-xl text-lg font-semibold border border-white/30 text-white hover:bg-white/10 transition-all shadow-lg flex items-center justify-center gap-2"
                            >
                                {t('university.cta.contact_us')}
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Auth Dialog */}
            <AuthDialog
                open={showAuthDialog}
                onClose={() => setShowAuthDialog(false)}
                initialMode={initialAuthMode}
                onSuccess={() => {
                    setShowAuthDialog(false);
                }}
            />
        </div>
    );
};

export default UniversityLanding;
